import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, RotateCcw } from 'lucide-react';
import { cn } from 'lib/utils';
import type { VocabWord } from 'modules/vocabulary';

/** "IELTS · Mock 3": the exam and test a word was saved from, or '' when that is unknown. */
export function sourceLabel(item: VocabWord): string {
  const source = item.source;
  if (!source) return '';
  return [source.standard, source.title].filter(Boolean).join(' · ');
}

/** The word is new until the learner marks it learned. */
export function StatusChip({ learned }: { learned: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 rounded-lg px-[9px] py-1 text-[11.5px] font-bold',
        learned ? 'bg-[#E6F4EC] text-[#166534] dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-muted text-foreground/80',
      )}
    >
      {t(learned ? 'vocab.status_learned' : 'vocab.status_new')}
    </span>
  );
}

interface LearnedToggleProps {
  item: VocabWord;
  onToggle: () => void;
  /** Text button on the phone card, icon-only in the desktop row. */
  variant: 'text' | 'icon';
}

/** Marks the word learned, or back to new. The label names the word so a screen reader hears which one. */
export function LearnedToggle({ item, onToggle, variant }: LearnedToggleProps) {
  const { t } = useTranslation();
  const learned = item.learned === true;
  const label = t(learned ? 'vocab.unmark_learned' : 'vocab.mark_learned', { word: item.word });
  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-pressed={learned}
        title={t(learned ? 'vocab.undo_action' : 'vocab.learned_action')}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {learned ? <RotateCcw className="h-4 w-4" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      className="inline-flex h-11 items-center rounded-xl border border-border bg-card px-3 text-xs font-bold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {t(learned ? 'vocab.undo_action' : 'vocab.learned_action')}
    </button>
  );
}
